/**
 * externalDragStore — vanilla singleton for cross-boundary drag state
 * (list panel → timeline). No Zustand overhead; uses a simple pub/sub pattern.
 */

let state = {
    isDragging: false,
    item: null,
    x: 0,
    y: 0,
};

const listeners = new Set();

function notify() {
    listeners.forEach(fn => fn({ ...state }));
}

let escapeHandler = null;

export function startExternalDrag(item, x, y) {
    state = { isDragging: true, item, x, y };
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';

    escapeHandler = (e) => {
        if (e.key === 'Escape') endExternalDrag();
    };
    document.addEventListener('keydown', escapeHandler);

    notify();
}

/**
 * Arms a drag without starting it: the drag begins only once the pointer has
 * actually travelled `threshold` pixels. A press that never moves stays a
 * click, so a list item can be both dragged onto the timeline and clicked to
 * select it — and releasing over the list no longer leaves a ghost behind.
 */
export function armExternalDrag(item, event, threshold = 4) {
    const startX = event.clientX;
    const startY = event.clientY;

    const cleanup = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', cleanup);
    };

    const onMove = (e) => {
        if (Math.abs(e.clientX - startX) < threshold && Math.abs(e.clientY - startY) < threshold) return;

        cleanup();
        startExternalDrag(item, e.clientX, e.clientY);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', cleanup);
}

export function updateExternalDragPosition(x, y) {
    if (!state.isDragging) return;
    state = { ...state, x, y };
    notify();
}

export function endExternalDrag() {
    if (!state.isDragging) return;
    state = { isDragging: false, item: null, x: 0, y: 0 };
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    if (escapeHandler) {
        document.removeEventListener('keydown', escapeHandler);
        escapeHandler = null;
    }

    notify();
}

export function getExternalDragState() {
    return { ...state };
}

export function subscribeExternalDrag(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}
